import React from "react"
import { graphql, Link } from "gatsby"
import { Helmet } from "react-helmet"

import Layout from "../../components/Layout"
import ServiceProof from "../../components/ServiceProof"
// The landing page's reveal observer, reused rather than reimplemented — see
// section 5 below for why this page now carries it.
import ScrollReveal from "../../components/home/ScrollReveal"
import { getOffer } from "../../data/offers"

import "../../styles/servicePage.scss"

/**
 * /Odoo/implementationPartner/ — the Odoo practice.
 *
 * Rebuilt to the /services/aisolution/ template: nine sections in the same
 * order, the same devices, the same .sp- system. It is the fifth and last of
 * the service pages to move; the page it replaces was the only one still on the
 * old construction — an OdooTopSection hero, a six-card "Why Businesses Choose
 * Visiomate for Odoo ERP & CRM Implementation" grid, a feature strip, the
 * OdooBenefitCards mosaic, the RecentSuccessStories strip, the ten FAQs inside
 * Wethoughtyoumighthavequestions, a dark CTA banner with a modal, the News
 * strip and a ServicePageOffers block.
 *
 * INDEXED AND RANKING. Frozen and byte-identical to git HEAD: the <h1>, the
 * <title>, the meta description, the canonical and the URL. The h1 came from
 * OdooTopSection's headerData.title and is now inline, written with entities so
 * the rendered characters are unchanged.
 *
 * ONE h1, down from three — antd's Typography.Title defaults to h1, and the
 * hero, the "Why Businesses Choose Visiomate" heading and the benefits heading
 * all used it.
 *
 * NO WEEK NUMBERS, NO DATES, NO FIXED-TIMELINE CLAIM. One FAQ answer carried
 * "from a few weeks to several months"; a duration on a service page is banned
 * by the same rule that took the onboarding figure off
 * /dynamics365/financeOperations/, so it now turns on what actually drives it
 * and points at the fixed-scope offers for a date. Reported, not decided
 * quietly.
 *
 * ── THE TEN FAQs ARE KEPT, rewritten to second person, and they are IN THE DOM.
 * Wethoughtyoumighthavequestions rendered them inside an antd Collapse, which
 * emits no answer text until a panel is opened — invisible to a crawler on a
 * page whose FAQPage JSON-LD claimed the answers were there. Both schema blocks
 * survive and are generated from the same array the section renders, so the
 * structured data cannot drift from the visible copy.
 *
 * ── ONE SUBSTANTIVE FAQ CORRECTION. The old answer said Odoo has "no licensing
 * fees". That is true of the Community edition and false of Enterprise, and it
 * contradicts src/data/offers.js, where every Odoo offer excludes "Odoo licence
 * fees (paid directly to Odoo)". The answer now says which edition carries no
 * fee. Reported.
 *
 * ── PROOF IS MUSCAT MEDIA GROUP, and which case studies qualify was checked
 * rather than assumed. Four project case studies name a client AND mention
 * Odoo: muscatMediaGroup (Odoo 13 HR build), emeAquaticSystems (Odoo
 * procurement, BOQs, multi-currency), bigJerrysFencing (Odoo CRM, Odoo
 * Projects, customer portal) and — not qualifying — orangelinePakistan, which
 * names Odoo zero times and is therefore not evidence of Odoo work.
 * nurturefulPsychology is a web build. The six pages under /caseStudies/products/
 * name no client at all.
 *
 * Big Jerry's metrics are already spent: the AI pair on /services/aisolution/
 * and the field pair on /services/mobiledevelopment/. EME's three figures do
 * not lead with a splittable numeral ("3-Stage", "Real-Time"). Muscat Media
 * Group is the one unspent Odoo client whose own case study carries a figure,
 * and it is the client the previous version of this page already featured.
 *
 * Its third case-study metric — "100% HR workflow automated" — is deliberately
 * NOT carried here. A 100% claim is absolute and this page introduces none;
 * the same substance is carried as a note without the numeral. Reported.
 *
 * ── UGPM STAYS. United Gulf Pipe Manufacturing had no case study route and
 * appeared only in the RecentSuccessStories strip, which is gone. The
 * engagement is named in the manufacturing capability instead, so the client,
 * "multi-step manufacturing" and the CRM/Sales/Purchase/Manufacturing module
 * list all survive the rebuild.
 *
 * Prices appear in ONE place: the offer rows in section 6.
 *
 * Now unreferenced by this page: OdooTopSection, WhyChooseVisiomateforD365,
 * RecentSuccessStories, Wethoughtyoumighthavequestions, ServiceCard, InfoModal,
 * IconsAdded, SimplifiedSteps, Simplified_div, ServicePageOffers,
 * OdooBenefitCards, News. All are left on disk untouched — several still have
 * other consumers, and OdooBenefitCards and odooBenefits.scss now have none.
 */

// SIX offers, not three. This is the largest practice in the catalogue: five
// Odoo builds plus the diagnostic that decides between them.
const OFFER_SLUGS = [
  "odoo-for-manufacturers",
  "odoo-for-multi-store-retail",
  "odoo-for-commerce-and-payments",
  "odoo-for-mep-contractors",
  "odoo-for-events-and-production",
  "ops-and-erp-fit-sprint",
]

// Resolved from the catalogue so a price, a timeline or a slug can never drift
// out of sync with the offer page it points at.
const PRACTICE_OFFERS = OFFER_SLUGS.map(getOffer).filter(Boolean)

/* 2. Six problems, in the reader's words — one from each offer in section 6,
   taken from that offer's own symptoms so no new claim is introduced here.

   DELIBERATELY NOT symptoms[0] ON ANY OF THEM. Section 6 renders symptoms[0]
   under each offer name, so drawing these from the same index would print six
   near-identical lines twice on one page. Each is that offer's second or fifth
   symptom instead. */
const PROBLEMS = [
  "You find out a job lost money after it shipped.",
  "The tills stop taking money when the internet drops.",
  "You sell something you do not have, because stock updates overnight rather than live.",
  "You buy in three currencies and the project cost depends on who did the maths.",
  "You are running twenty jobs and can only see margin on the ones that finished.",
  "You cannot tell whether you need a new system or a fix to the one you already have.",
]

/* 4. The capabilities, with the modules named rather than implied. The stack
   chips carry the module and discipline terms the old feature strip held. */
const CAPABILITIES = [
  {
    title: "One platform where finance, sales, stock and people agree",
    body:
      "The whole point of a suite is that a sale moves the stock, the stock moves the ledger and nobody retypes anything in between. Accounting, sales, inventory, projects and an HR management system with employee self-service run as one platform on one set of customer data and one set of orders — which is what you are buying instead of separate ERP and CRM tools stitched together.",
    stack: [
      "ERP Solution",
      "CRM Solution",
      "Accounting",
      "Sales",
      "Inventory Management",
      "Human Resources (HR)",
      "Project Management",
    ],
  },
  {
    title: "Production that matches what you actually build",
    body:
      "Bills of materials, routings and purchase running against real products rather than a spreadsheet nobody trusts, with traceability from a batch back to the shipment it went into. This is the configuration built for United Gulf Pipe Manufacturing (UGPM), where a multi-step manufacturing flow put CRM, Sales, Purchase and Manufacturing into one connected ERP.",
    stack: [
      "Manufacturing",
      "Bills of materials",
      "Purchase",
      "Inventory Management",
      "Batch traceability",
    ],
  },
  {
    title: "A counter and a website working from one stock figure",
    body:
      "Point of sale across your outlets and an online store on the same inventory, so the number on the product page is the number in the warehouse and a refund is not a spreadsheet entry. Payment gateways are wired to the ledger, which is what turns a monthly reconciliation into something that has already happened.",
    stack: [
      "Point of Sale (POS)",
      "E-commerce",
      "Payment gateways",
      "Inventory Management",
      "Accounting",
    ],
  },
  {
    title: "Custom modules, where configuration runs out",
    body:
      "Most of what people expect to pay for as code is a configuration change, and you should be told which is which before the quote. Where a genuine gap remains, custom modules extend the standard behaviour — workflows, reports and user interfaces built to your specification, then documented and tested for maintainability and upgrade compatibility rather than left as somebody's clever trick.",
    stack: [
      "Custom Modules",
      "Workflows",
      "Reports",
      "User interfaces",
      "Upgrade compatibility",
    ],
  },
  {
    title: "Third-party integrations for the systems you keep",
    body:
      "Odoo does not have to be the only thing you run. Third-party integrations connect it to the e-commerce platforms, payment gateways, logistics providers and HR tools you already depend on, through an integration architecture designed for the traffic you actually have rather than for a demonstration.",
    stack: [
      "Third-Party Integrations",
      "Payment gateways",
      "Logistics providers",
      "HR tools",
      "Integration architecture",
    ],
  },
  {
    title: "Data migration off spreadsheets and legacy systems",
    body:
      "Your historical records come out of legacy systems, spreadsheets and other ERPs through data mapping, cleansing, validation and import, with data accuracy checked against the source before anyone relies on it. Migration is where implementations quietly fail, which is why what arrives is reconciled rather than assumed.",
    stack: [
      "Data Migration",
      "Data mapping",
      "Cleansing",
      "Validation",
      "Historical records",
    ],
  },
]

/* 5. THE ODOO APPS STORE — third-party verified capability.
   Odoo reviews and approves every listing, so this is the one claim on the page
   that is not ours to make or to overstate. Names, descriptions and URLs are
   reproduced exactly as given and are not editorial.

   NO PRICES. Every module is paid and Odoo lists each price in USD on its own
   store page; a USD figure here would sit beside the offer rows in section 7 and
   read as part of our own pricing. The link goes out and Odoo shows its price.

   NO INSTALL COUNTS, RATINGS OR REVIEW NUMBERS — none is published here because
   none was supplied, and the store is the authority on all three. */
const APPS = [
  {
    name: "GoCardless Payment Integration",
    desc: "Accept Direct Debit payments via GoCardless (BACS, SEPA, ACH)",
    url: "https://apps.odoo.com/apps/modules/19.0/visio_payment_gocardless",
  },
  {
    name: "Klarna Payment Method",
    desc:
      "Accept Klarna payments in Odoo 19 — Pay Now, Pay Later, and Pay over Time",
    url: "https://apps.odoo.com/apps/modules/19.0/klarna_payment_method_vm",
  },
  {
    name: "Revolut Payment Integration",
    desc: "Integrate Odoo with the Revolut Merchant payment method",
    url: "https://apps.odoo.com/apps/modules/19.0/revoult_payment_method_vm",
  },
]

const APPS_ALL_URL = "https://apps.odoo.com/apps/modules/browse?author=Visiomate"

/* 5. Named phases, no numbers and no dates. This page exists for open-ended
   work: a phase is a shape of activity, not a slot in a calendar. */
const PHASES = [
  {
    phase: "Before anything is installed",
    title: "Requirement analysis and business process mapping",
    body:
      "You get your own operation written down before a module is switched on: how an order actually moves today, who touches it, and which of those steps exists only because the current tools cannot do it. Business process mapping with the people who run the process, and the stakeholders who will have to sign it off, is what stops a configuration being built around an org chart instead of a business.",
  },
  {
    phase: "Agreeing the shape",
    title: "Scope written down, then held",
    body:
      "Which modules go live, what gets configured, what needs custom modules and what is deliberately left out — agreed in writing, with the objectives it is meant to serve stated in your numbers. Change is handled explicitly: anything new is quoted as an addition rather than absorbed silently, which is what keeps the risk and the time-to-value where they were when you approved them.",
  },
  {
    phase: "While it is being configured",
    title: "Something to open every cycle",
    body:
      "Progress is a system you can log into, not a status report. Configuration, custom modules and migrated data land in a test environment in short cycles, and you see your own records in it early enough that steering is still cheap.",
  },
  {
    phase: "Before anyone relies on it",
    title: "Tested, trained and signed off",
    body:
      "Testing and quality assurance run against your processes rather than a checklist, and user acceptance testing is your team confirming the thing works the way they were told it would. Role-specific user training and the change management around it are planned rather than bolted on at the end, because adoption is what decides whether any of this was worth doing. Where the shape of your business calls for it, a phased rollout department by department is the safer path than one company-wide switch.",
  },
  {
    phase: "After go-live",
    title: "Post go-live support and optimisation",
    body:
      "Deployment into a production environment is the start of the arrangement rather than the end of it. Support and maintenance, system performance monitoring, the issues your users actually raise and optimisation as your processes move are all part of what happens next, alongside the upgrades that arrive whether you planned for them or not.",
  },
]

/* 7. Proof from this discipline only, and only where a case study page exists.
   See the file header for which case studies qualify and why this is the one
   used. The figures and the wording are the client's and are quoted, not
   rewritten — except that the case study's "100%" metric is carried without its
   numeral, deliberately. */
const PROOF = {
  client: "Muscat Media Group",
  path: "/caseStudies/projects/muscatMediaGroup",
  metrics: [
    "6 custom Odoo modules built from scratch for the HR department's own pain points",
    // NO NUMERAL EXISTS IN EITHER OF THESE and none is invented. ServiceProof
    // renders them below the stat row rather than under an empty numeral slot.
    "HR reports that used to take hours now come out of live data in minutes",
    "A complete migration off manual spreadsheets onto a digital Odoo system, covering leave, attendance, gratuity and employee self-service",
  ],
}

/* 8. THE TEN FAQs, KEPT. Rewritten from the third person about the market —
   "Organizations can seek assistance", "Implementing Odoo offers several
   benefits" — to second person about the reader. Substance unchanged except the
   two edits recorded in the file header: the removed duration in "How long
   does an implementation take", and the licence-fee correction. */
const FAQS = [
  {
    q: "What is Odoo?",
    a: "It is an open-source suite of integrated business applications you run your operation on, rather than a single product for one job. The same suite covers CRM, sales, inventory, manufacturing, accounting, HR and more, and it is modular — you select and customise the applications that fit how you work instead of taking a fixed bundle.",
  },
  {
    q: "Why would you put your business on Odoo rather than something else?",
    a: "Five reasons account for most of it:",
    bullets: [
      "A comprehensive suite: one set of integrated business applications across your functional areas, so you are not paying for and reconciling multiple disparate systems.",
      "Scalability: start with a few modules and add more as your business grows or your requirements change.",
      "Cost: the Community edition carries no licensing fee at all, and even on Enterprise the total cost of ownership tends to sit below comparable proprietary software once implementation and customisation are counted.",
      "Customisation: modules, workflows, reports and user interfaces can be modified or added to meet requirements the standard configuration does not reach.",
      "Community and support: a large body of users, developers and partners, which matters most on the day you need an answer nobody has written down yet.",
    ],
    after:
      "None of those is a reason on its own. Whether they add up for you depends on your processes, which is what the fit sprint above exists to answer.",
  },
  {
    q: "What modules are included in Odoo?",
    a: "The suite covers most functional areas you would otherwise buy separately:",
    bullets: [
      "CRM (Customer Relationship Management)",
      "Sales",
      "Inventory Management",
      "Manufacturing",
      "Accounting",
      "Human Resources (HR)",
      "Project Management",
      "E-commerce",
      "Point of Sale (POS), and many more",
    ],
    after:
      "You do not turn all of them on. Most implementations start with three or four and grow.",
  },
  {
    q: "What is actually involved in implementing Odoo?",
    a: "Installing the software is the smallest part of it. A typical implementation runs through:",
    bullets: [
      "Requirement analysis: what your business needs, how your processes actually run, and what that makes the scope.",
      "Configuration: modules, workflows and settings set up against those processes rather than against a default.",
      "Data migration: your existing data brought in from legacy systems or spreadsheets, checked for accuracy and completeness.",
      "Customisation: custom modules, enhancements or integrations where the standard behaviour genuinely does not reach.",
      "Training: role-specific training and the user-adoption work that decides whether any of it gets used.",
      "Testing: functional, performance and usability testing, including quality assurance and user acceptance testing.",
      "Deployment: go-live into a production environment, then ongoing support and maintenance.",
    ],
  },
  {
    q: "How long does an Odoo implementation take?",
    // The old answer said "from a few weeks to several months". A duration on a
    // service page is banned, so it now turns on what actually drives it.
    a: "It depends on how many modules you are turning on, how complex your processes are, how much customisation you need and how ready your organisation is to change how it works — which is why no duration is published for open-ended work here. Where a phased rollout suits you better than one cutover, the sequencing is part of the scope rather than an afterthought. The fixed-scope options above each carry their own timeline, and if a date you can hold someone to matters more than an exact fit, start there.",
  },
  {
    q: "How do you make sure your implementation actually succeeds?",
    a: "The failures are rarely technical. What separates the implementations that land from the ones that stall:",
    bullets: [
      "Clear objectives, goals and success criteria agreed before the work starts, in numbers you can check afterwards.",
      "Stakeholders engaged and the key decision-makers bought in, rather than informed at go-live.",
      "Thorough requirement analysis, so what is being built is your process and not a guess at it.",
      "Real planning for training, change management and user adoption, because organisational readiness is what actually decides this.",
      "Experienced Odoo implementation partners or consultants who know your industry and your business processes.",
      "Clear communication channels, project milestones and accountability, so a problem surfaces while it is still small.",
    ],
  },
  {
    q: "Where can you get help with an Odoo implementation?",
    a: "From certified Odoo partners, consultants and solution providers, who between them cover implementation, customisation, training and support. Visiomate is an Odoo Ready Partner and does this work; you should still ask any provider which industries they have delivered in and which of their people would be on your project, because that is the answer that separates them.",
  },
  {
    q: "Is Odoo a good ERP if you are a small or mid-sized business?",
    a: "It is often a strong fit for small and mid-sized businesses, and being modular and cost-effective is why. You start with the ERP functions you need — finance, inventory, sales — and add modules as you grow, rather than carrying the heavy licensing cost of traditional enterprise ERP systems for capability you will not use for two years.",
  },
  {
    q: "Can Odoo be both your ERP and your CRM?",
    a: "Yes, and it is one of the stronger arguments for it. The CRM sits on the same platform as the ERP modules, so your sales pipeline, customer data, orders, inventory and finance connect in one system instead of being separate tools that need integrating — and the integration between them is not a project you have to pay for.",
  },
  {
    q: "How does Odoo compare with other ERP or CRM systems?",
    a: "It covers both ERP and CRM in a single customisable platform, usually at a lower total cost than the equivalent standalone systems. Whether that makes it the right answer for you depends on your processes, not on the feature list — so the honest sequence is to have your needs assessed first and be told plainly if something else fits better. A recommendation to look elsewhere is a more useful outcome than a forced fit.",
  },
]

const SITE = "https://www.visiomate.com"

// Both schema blocks are generated from the FAQS array the page renders, so the
// structured data cannot drift from the visible copy the way it did while the
// answers lived inside a collapsed antd panel.
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: [f.a, ...(f.bullets || []), f.after].filter(Boolean).join(" "),
    },
  })),
}

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "ERP and CRM Implementation",
  provider: { "@type": "Organization", name: "Visiomate", url: SITE },
  url: `${SITE}/Odoo/implementationPartner`,
}

const OdooImplementationPartner = ({ data }) => (
  <Layout
    className="services-page-container"
    data={data}
    // FROZEN — byte-identical to git HEAD. Do not touch.
    pageTitle={"ERP & CRM Implementation Partner | Powered by Odoo | Visiomate"}
    descrip={
      "Visiomate implements Odoo to cover your ERP and CRM needs in one integrated platform — expert implementation, customization, and support for growing businesses."
    }
  >
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
    </Helmet>

    <article className="sp">
      {/* ── 1. HERO — FULL WIDTH, no split, no rail ─────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero__inner">
          <span className="sp-tag">Odoo practice</span>
          {/* FROZEN h1. The only h1 on the page. Entities render the same
              characters the OdooTopSection title carried. */}
          <h1 className="sp-hero__h1">
            ERP &amp; CRM Implementation with Odoo &mdash; Certified Partners,
            Proven Results
          </h1>
          <p className="sp-hero__lede">
            You are running the business on more systems than agree with each
            other, and the reconciling is being done by people. What you do not
            have is one place where a sale, the stock behind it and the ledger
            underneath are the same record. That is the gap this practice
            closes — Odoo configured to how your operation actually runs, not a
            default you bend yourself around.
          </p>
          <Link to="/contact" className="sp-hero__cta">
            Talk through what you are dealing with
          </Link>
        </div>
      </section>

      {/* ── 2. PROBLEMS — two columns, hairlines, no numbers, no cards ──────── */}
      <section className="sp-problems">
        <div className="sp-problems__inner">
          <span className="sp-tag">Where this starts</span>
          <h2 className="sp-h2">The problems this practice solves</h2>
          <p className="sp-lede">
            Almost none of these are software faults. They are the cost of
            running a business across systems that were never meant to talk to
            each other, and it is paid every month in hours nobody bills for.
          </p>
          <ul className="sp-problems__grid">
            {PROBLEMS.map(item => (
              <li className="sp-problems__item" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 3. WHAT THIS ACTUALLY IS — the section offer pages do not have ──── */}
      <section className="sp-what">
        <div className="sp-what__inner">
          <div className="sp-what__cols">
            <div className="sp-what__lead">
              <span className="sp-tag">In plain terms</span>
              <h2 className="sp-h2">What this actually is</h2>
              <p className="sp-what__standfirst">
                Four things get confused in almost every Odoo conversation: what
                the thing is, what a module is, which edition you are being
                quoted, and what implementation actually means. None of them is
                complicated, and all four change the number at the end.
              </p>
            </div>
            <div className="sp-what__body">
              <div className="sp-what__part">
                <h3 className="sp-what__parttitle">
                  A suite, not a single product
                </h3>
                <p>
                  Odoo is an open-source suite of integrated business
                  applications. Compare it with a single accounting package or a
                  standalone CRM: each of those does one job well and then hands
                  off to something else, and the handing off is the part you pay
                  for forever — in exports, in re-keying, in two systems that
                  disagree about the same customer. In a suite, the sale, the
                  stock movement and the accounting entry are the same event seen
                  from three places. That is the whole of the difference, and it
                  is also why the honest question about Odoo is never about
                  features but about whether you can get your processes into one
                  system.
                </p>
              </div>
              <div className="sp-what__part">
                <h3 className="sp-what__parttitle">
                  What a module is, and why you only pay for what you turn on
                </h3>
                <p>
                  A module is one of those applications — CRM, Sales, Inventory
                  Management, Manufacturing, Accounting, Human Resources (HR),
                  Project Management, E-commerce, Point of Sale (POS) — and it is
                  a switch rather than a purchase order. You turn on what you use
                  and leave the rest off, so a business needing sales, stock and
                  accounting is not carrying manufacturing it will not open. This
                  matters twice. It is why a first phase can be genuinely small,
                  and it is why the cost of a system grows with adoption rather
                  than arriving all at once. It is also where the estimates you
                  have been given probably diverge: two proposals quoting
                  different module sets are not quoting the same system.
                </p>
              </div>
              <div className="sp-what__part">
                <h3 className="sp-what__parttitle">
                  Community versus Enterprise
                </h3>
                <p>
                  There are two editions, and knowing which one a quote assumes
                  is worth more than most of the rest of the quote. Odoo Community
                  Edition is the open-source one: no licence fee, self-hosted, and
                  a smaller set of applications. Enterprise is the paid
                  subscription, billed per user, and it adds applications
                  Community does not carry along with Odoo&rsquo;s own hosting,
                  version upgrades and vendor support. Neither is the right answer
                  in general. Community suits a business with somebody technical
                  and a tolerance for owning its own upgrades; Enterprise suits
                  one that would rather buy that away. Which applications sit in
                  which edition moves between releases, so the comparison is only
                  meaningful against the version you would actually run — and
                  either way the licence, where there is one, is paid to Odoo
                  rather than to an implementation partner.
                </p>
              </div>
              <div className="sp-what__part">
                <h3 className="sp-what__parttitle">
                  What implementation involves beyond installation
                </h3>
                <p>
                  Installing Odoo is an afternoon. Implementation is the other
                  thing entirely, and it is what the money and the risk are in:
                  your processes examined and written down, modules configured
                  against them, your history migrated out of spreadsheets and
                  legacy systems and reconciled, the integrations built to the
                  systems you are keeping, custom modules only where
                  configuration genuinely runs out, then testing, user acceptance
                  testing, role-specific training, and support after go-live. Put
                  plainly: installation gives you the software, and
                  implementation is the work of turning your business into
                  something the software can run. Skip the second and you have
                  bought a licence and kept the problem.
                </p>
              </div>
            </div>
          </div>
          <p className="sp-what__pull">
            The useful question is never whether Odoo can do it — it very
            probably can. It is which of your processes you are willing to change
            to fit the standard behaviour, because every one you will not change
            becomes custom work you own for as long as you run the system.
          </p>
        </div>
      </section>

      {/* ── 4. WHAT GETS BUILT WITH IT — THE DARK BAND, cards ───────────────── */}
      <section className="sp-build">
        <div className="sp-build__inner">
          <span className="sp-tag">The capabilities</span>
          <h2 className="sp-h2">What gets built with it</h2>
          <p className="sp-lede">
            Six things account for nearly every engagement in this practice. Most
            implementations are two or three of them wired together, not all six.
          </p>
          <div className="sp-build__grid">
            {CAPABILITIES.map(cap => (
              <div className="sp-build__card" key={cap.title}>
                <h3 className="sp-build__cardtitle">{cap.title}</h3>
                <p className="sp-build__cardbody">{cap.body}</p>
                <ul className="sp-build__stack">
                  {cap.stack.map(tech => (
                    <li className="sp-build__stackitem" key={tech}>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. ODOO APPS STORE — third-party verified capability ─────────────
             NEW SECTION. It sits here on purpose: after the capabilities and
             before the process, so it reads as evidence of what section 4
             claims rather than as a pitch.

             CONSTRUCTION (DESIGN-SYSTEM §3). Neither the ground nor the padding
             is shared with either neighbour:
               4  build   $deep    $s-12   ← the dark band
               5  apps    $paper   $s-8    ← this section
               6  how     $white   $s-12

             The cards are deliberately NOT section 4's cards. Those are
             translucent filled tiles on $deep at $r-md/$s-4 with no hover,
             because they do not link anywhere. These are outbound references:
             $white on $paper, hairline border, the smaller $r-sm radius, and a
             persistent ↗ so the new tab is signalled without hover. Hover and
             focus use the page's existing convention — $dur-fast/$ease and a
             2px $signal focus ring — not a new one.

             THIS PAGE NOW CARRIES ScrollReveal. It is the landing page's own
             component, imported rather than copied, and only these three cards
             opt in via [data-reveal]. Note this is the first use of the reveal
             system outside the landing page; the sitewide rollout (motion audit
             Gate 1) is still unstarted and unapproved.
           ──────────────────────────────────────────────────────────────────── */}
      <ScrollReveal />
      <section className="sp-apps">
        <div className="sp-apps__inner">
          <span className="sp-tag">On the Odoo Apps Store</span>
          <h2 className="sp-h2">Ten of our modules are published by Odoo itself</h2>
          <p className="sp-lede">
            You can install any of them as a paid module from the official Apps
            Store, where Odoo reviews and lists every one, and the team that
            builds them is the team that implements your system.
          </p>
          <ul className="sp-apps__grid">
            {APPS.map((app, i) => (
              <li
                className="sp-apps__item"
                key={app.name}
                data-reveal
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <a
                  className="sp-apps__card"
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="sp-apps__name">{app.name}</span>
                  <span className="sp-apps__desc">{app.desc}</span>
                  <span className="sp-apps__meta">
                    Odoo Apps Store
                    <span className="sp-apps__ext" aria-hidden="true">
                      &#8599;
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <p className="sp-apps__all">
            <a href={APPS_ALL_URL} target="_blank" rel="noopener noreferrer">
              See all ten modules on the Odoo Apps Store &rarr;
            </a>
          </p>
        </div>
      </section>

      {/* ── 6. HOW WE WORK — stepped across, named phases, no week numbers ─── */}
      <section className="sp-how">
        <div className="sp-how__inner">
          <span className="sp-tag">How the work runs</span>
          <h2 className="sp-h2">How open-ended work is run</h2>
          <p className="sp-lede">
            An implementation scoped to your own processes rarely arrives with a
            date attached, so what you get instead of a delivery date is
            visibility: agreed scope, something to open every cycle, and a way
            out.
          </p>
          <ol className="sp-how__steps">
            {PHASES.map(ph => (
              <li className="sp-how__step" key={ph.title}>
                <span className="sp-how__marker" aria-hidden="true" />
                <span className="sp-how__phase">{ph.phase}</span>
                <h3 className="sp-how__title">{ph.title}</h3>
                <p className="sp-how__body">{ph.body}</p>
              </li>
            ))}
          </ol>
          <p className="sp-how__note">
            No date is published for work of this shape, and none is implied. If
            what you need is a scope and a date you can hold someone to, the
            packages below carry both.
          </p>
        </div>
      </section>

      {/* ── 7. FIXED-SCOPE OPTIONS — rows, prices shown ─────────────────────── */}
      <section className="sp-offers">
        <div className="sp-offers__inner">
          <span className="sp-tag">Fixed-scope options</span>
          <h2 className="sp-h2">Some of this work has a written scope already</h2>
          <p className="sp-lede">
            Five Odoo builds and one diagnostic have been delivered enough times
            to state upfront what they contain, how long they take and what they
            cost. If you cannot yet tell which of them you need, the last one
            answers that first.
          </p>
          <ul className="sp-offers__list">
            {PRACTICE_OFFERS.map(o => (
              <li className="sp-offers__item" key={o.slug}>
                <Link to={`/offers/${o.slug}`} className="sp-offers__row">
                  <span className="sp-offers__main">
                    <span className="sp-offers__name">{o.name}</span>
                    <span className="sp-offers__problem">{o.symptoms[0]}</span>
                  </span>
                  <span className="sp-offers__meta">
                    <span className="sp-offers__timeline">{o.timelineShort}</span>
                    <span className="sp-offers__price">{o.priceShort}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sp-offers__all">
            <Link to="/offers">Every offer, across every practice &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ── 8. PROOF — the shared component. Muscat Media Group; see the file
             header for which case studies qualify as Odoo work and why this is
             the one used. components/ServiceProof.js ────────────────────────── */}
      <ServiceProof
        client={PROOF.client}
        path={PROOF.path}
        metrics={PROOF.metrics}
      />

      {/* ── 9. QUESTIONS — the ten kept FAQs, in the DOM ────────────────────── */}
      <section className="sp-faq">
        <div className="sp-faq__inner">
          <span className="sp-tag">Questions</span>
          <h2 className="sp-h2">What buyers ask about Odoo work</h2>
          <ul className="sp-faq__list">
            {FAQS.map(f => (
              <li className="sp-faq__item" key={f.q}>
                <p className="sp-faq__q">{f.q}</p>
                <p className="sp-faq__a">{f.a}</p>
                {f.bullets && (
                  <ul className="sp-faq__bullets">
                    {f.bullets.map(bi => (
                      <li className="sp-faq__bullet" key={bi}>
                        {bi}
                      </li>
                    ))}
                  </ul>
                )}
                {f.after && <p className="sp-faq__after">{f.after}</p>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 10. IF YOUR NEED IS BROADER — one CTA ───────────────────────────── */}
      <section className="sp-broader">
        <div className="sp-broader__inner">
          <span className="sp-tag">If your need is broader</span>
          <h2 className="sp-h2">If none of that is quite your situation</h2>
          <div className="sp-broader__cols">
            <p className="sp-broader__text">
              Plenty of what lands in this practice does not fit a package: an
              implementation somebody else started and left short of live, a
              version too old to upgrade in place, custom modules written by a
              partner who has moved on and documented nothing, or a licence bill
              nobody can reconcile against the modules people actually open.
            </p>
            <p className="sp-broader__text">
              Describe what you are dealing with and you will get a straight read
              on how it would be approached, what it depends on, and where the
              risk sits. If the answer is that Odoo is the wrong system for you,
              that is what you will hear.
            </p>
          </div>
          <div className="sp-broader__foot">
            <Link to="/contact" className="sp-broader__cta">
              Tell us what you are dealing with
            </Link>
          </div>
        </div>
      </section>
    </article>
  </Layout>
)

export default OdooImplementationPartner

export const odooImplementationQuery = graphql`
  query OdooImplementationImages {
    bgImage: file(relativePath: { eq: "Mask_group8.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
